<template>
  <section class="tw:flex tw:max-w-[520px] tw:flex-col tw:gap-7" data-testid="reg-step-verify_email">
    <RegistrationStepHeading :title="$t('registration.verify.title')">
      {{ $t('registration.verify.lede_before') }}
      <strong class="tw:font-semibold tw:text-gray-900" data-testid="reg-verify-address">{{ email }}</strong>
      {{ $t('registration.verify.lede_after', { minutes: expiryMinutes }) }}
    </RegistrationStepHeading>

    <div class="tw:flex tw:flex-col tw:gap-3">
      <CodeInput
        ref="codeRef"
        v-model="code"
        :has-error="Boolean(error)"
        :disabled="store.isSubmitting"
        :aria-label="$t('registration.verify.code_label')"
        data-testid="reg-code"
        @complete="onComplete">
        <template #aside>
          <ResendCountdown
            ref="countdownRef"
            :seconds="store.verification.resendAfterSeconds"
            :busy="isResending"
            @resend="resend" />
        </template>
      </CodeInput>

      <span v-if="error" class="tw:text-13 tw:text-danger" role="alert" data-testid="reg-code-error">{{ error }}</span>
    </div>

    <div
      class="tw:flex tw:max-w-[400px] tw:flex-col tw:gap-2.5 tw:rounded-md tw:border tw:border-border tw:bg-gray-50 tw:p-4">
      <span class="tw:text-13 tw:font-medium tw:text-gray-700">{{ $t('registration.verify.no_email') }}</span>
      <span class="tw:text-13 tw:text-gray-600 tw:[text-wrap:pretty]">
        {{ $t('registration.verify.check_spam_before') }}
        <button
          type="button"
          class="tw:font-medium tw:text-primary tw:hover:underline"
          data-testid="reg-change-address"
          @click="store.back()">
          {{ $t('registration.verify.different_address') }}
        </button>
        {{ $t('registration.verify.check_spam_after') }}
      </span>
    </div>
  </section>
</template>

<script setup lang="ts">
/** Step 2 - a correct code creates the account, records the terms acceptance and signs the registrant in */
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';

import { registrationClient } from '@/api/registration';
import CodeInput from '@/components/registration/subcomponents/CodeInput.vue';
import RegistrationStepHeading from '@/components/registration/subcomponents/RegistrationStepHeading.vue';
import ResendCountdown from '@/components/registration/subcomponents/ResendCountdown.vue';
import {
  registrationErrorMessage,
  verificationErrorMessage,
} from '@/components/registration/subcomponents/registrationErrors';
import { useRegistrationStore } from '@/stores/registration';
import { useUserStore } from '@/stores/user';

const CODE_LENGTH = 6;

/* ----------------------------------- STATE ---------------------------------- */
const allowNext = defineModel<boolean>('allowNext', { required: true });

const store = useRegistrationStore();
const userStore = useUserStore();

const code = ref('');
const error = ref('');
const isResending = ref(false);

const codeRef = useTemplateRef<InstanceType<typeof CodeInput>>('codeRef');
const countdownRef = useTemplateRef<InstanceType<typeof ResendCountdown>>('countdownRef');

/* --------------------------------- COMPUTED --------------------------------- */
const email = computed(() => store.draft.account.email);

/** stated in minutes because that is how the sentence reads */
const expiryMinutes = computed(() => Math.max(1, Math.round(store.verification.expiresInSeconds / 60)));

/* --------------------------------- METHODS ---------------------------------- */
/** typing the last digit submits; the footer button does the same for a pasted code */
function onComplete() {
  if (store.isSubmitting) return;
  store.next();
}

function messageFor(error: unknown) {
  return registrationErrorMessage(error, (code) => verificationErrorMessage(code, 'email'));
}

async function submit(): Promise<boolean> {
  if (code.value.length !== CODE_LENGTH) return false;

  error.value = '';

  try {
    await registrationClient.verifyEmail({ code: code.value });
  } catch (caught) {
    error.value = messageFor(caught);
    // clear only the code, not what the registrant typed
    code.value = '';
    return false;
  }

  // the wizard's guards read authentication off the user store, so tell it
  store.emailVerified = true;
  await userStore.fetchCurrent();

  return true;
}

async function resend() {
  if (isResending.value) return;

  isResending.value = true;
  error.value = '';

  try {
    const response = await registrationClient.resendEmailCode();
    store.setVerification({
      expiresInSeconds: response.expiresInSeconds,
      resendAfterSeconds: response.resendAfterSeconds,
    });
    // restart explicitly: a repeat resend often returns the same cooldown, so the watcher wouldn't fire
    countdownRef.value?.restart(response.resendAfterSeconds);
    code.value = '';
  } catch (caught) {
    error.value = messageFor(caught);
  } finally {
    isResending.value = false;
  }
}

/* -------------------------------- LIFECYCLE --------------------------------- */
onMounted(() => {
  store.availableSteps.verify_email.onContinue = submit;
  // the code field is the only thing to do, so focus it
  nextTick(() => codeRef.value?.focusFirst());
});

/* --------------------------------- WATCHERS --------------------------------- */
watch(
  code,
  (value) => {
    allowNext.value = value.length === CODE_LENGTH;
    // clear as soon as the registrant starts correcting
    if (error.value && value.length > 0) error.value = '';
  },
  { immediate: true }
);
</script>
